import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  CircularProgress,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import HomeNavbar from "../components/home-nav-bar";

function PeerReviewersPage() {
  const [reviewers, setReviewers] = useState([]);
  const [filteredReviewers, setFilteredReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    axios
      .get("https://api.wajls.com/client/v1/fetch_peer_reviewers.php")
      .then((res) => {
        setReviewers(res.data);
        setFilteredReviewers(res.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = reviewers.filter((r) =>
      r.personal_information?.name?.toLowerCase().includes(query)
    );
    setFilteredReviewers(filtered);
  }, [searchQuery, reviewers]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <HomeNavbar />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "#1a1a1a" }}>
            Peer Reviewers
          </Typography>
        </Box>

        {/* <TextField
          fullWidth
          placeholder="Search by name"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ 
            mb: 4, 
            backgroundColor: "white", 
            borderRadius: 2,
            maxWidth: 600,
            mx: "auto",
            display: "block",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        /> */}

        {loading ? (
          <Box display="flex" justifyContent="center" mt={5}>
            <CircularProgress />
          </Box>
        ) : filteredReviewers.length === 0 ? (
          <Box sx={{ 
            backgroundColor: "white", 
            p: 4, 
            borderRadius: 2, 
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
          }}>
            <Typography>No peer reviewers found.</Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {filteredReviewers.map((reviewer, idx) => {
              const personal = reviewer.personal_information;
              const experience = reviewer.experience;
              const expertise = reviewer.expertise;
              const availability = reviewer.availability_and_commitment;

              return (
                <Box 
                  key={idx}
                  sx={{ 
                    backgroundColor: "white", 
                    borderRadius: 2,
                    p: 3,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                      transform: "translateY(-2px)"
                    }
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                    {/* Avatar Section - with name/title on mobile */}
                    <Box display="flex" alignItems="flex-start" gap={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
                      <Avatar
                        src={personal.profile_image}
                        alt={personal.name}
                        sx={{ 
                          width: 80, 
                          height: 80, 
                          flexShrink: 0,
                          border: "3px solid #f0f0f0"
                        }}
                      />
                      
                      {/* Name and Title - only shows on mobile */}
                      <Box flex={1} minWidth={0} sx={{ display: { xs: 'block', md: 'none' } }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: "#1a1a1a" }}>
                          {personal.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {personal.title}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Main Content */}
                    <Box flex={1}>
                      {/* Name and Title - only shows on desktop */}
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: "#1a1a1a", display: { xs: 'none', md: 'block' } }}>
                        {personal.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, display: { xs: 'none', md: 'block' } }}>
                        {personal.title}
                      </Typography>
                      
                      {/* Contact and Affiliation Info */}
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <BusinessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                          <Typography variant="body2" color="text.secondary">
                            {personal.affiliation} {personal.department && `• ${personal.department}`}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmailIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                              {personal.email}
                            </Typography>
                          </Box>
                          
                          {personal.phone && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <PhoneIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                              <Typography variant="body2" color="text.secondary">
                                {personal.phone}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Stack>

                      {/* Expertise */}
                      {expertise && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#1a1a1a" }}>
                            Expertise
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {expertise}
                          </Typography>
                        </Box>
                      )}

                      {/* Experience Section */}
                      <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                        {experience?.papers_reviewed_count && (
                          <Box 
                            sx={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: 1,
                              backgroundColor: "#f8f9fa",
                              px: 2,
                              py: 1,
                              borderRadius: 1
                            }}
                          >
                            <AssignmentIcon sx={{ fontSize: 18, color: "primary.main" }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {experience.papers_reviewed_count} papers reviewed
                            </Typography>
                          </Box>
                        )}
                        
                        {availability && (
                          <Box 
                            sx={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: 1,
                              backgroundColor: "#f0f7ff",
                              px: 2,
                              py: 1,
                              borderRadius: 1
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {availability}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Paper Types */}
                      {experience?.paper_types && experience.paper_types.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#1a1a1a" }}>
                            Paper Types Reviewed
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {experience.paper_types.map((type, i) => (
                              <Chip 
                                key={i} 
                                label={type} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: "#e3f2fd",
                                  color: "#1976d2",
                                  fontWeight: 500
                                }}
                              />
                            ))}
                            {experience.otherPaper_types && (
                              <Chip 
                                label={experience.otherPaper_types} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: "#f3e5f5",
                                  color: "#7b1fa2",
                                  fontWeight: 500
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Container>
    </Box>
  );
}

export default PeerReviewersPage;